var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef } from 'react';
import styled, { css, } from 'styled-components';
import { compose, space, layout, border, color, typography, margin, } from 'styled-system';
import { getPrivateAttributes } from '@ally/private';
import { AdornmentProvider, usePasswordMaskAdornment, useAdornmentState, getAdornmentInputType, parseAdornmentOpts, Adornment, } from './Adornment';
import { InputSelect } from './InputSelect';
import { FormTextWrapper } from '../shared';
import { propsToForward } from '../../tools/utils';
// NOTE: height is not being added as a style prop to the component and is
// being added here instead since the styled-system `height` prop conflicts
// with the input DOM node `height` attribute. This was causing build errors.
// I think there is a way around this and plan on readdressing this later.
const StyledInput = styled.input.withConfig({
    shouldForwardProp: propsToForward,
}) `
  ${compose(space, layout, border, color, typography)}
  ${({ $hasCounter, $adornmentVariant, $adornmentPosition, theme: { space: themeSpace }, }) => css `
    height: 44px;
    min-width: 0;
    padding-right: ${$hasCounter ? themeSpace.xxl : '0px'};

    &&& {
      ${$adornmentPosition === 'left' ? 'border-top-left-radius: 0px;' : ''};
      ${$adornmentPosition === 'right' ? 'border-top-right-radius: 0px;' : ''};
    }

    /* disable default password mask toggle (Edge) if passwordMask adornment already exists */
    /* https://docs.microsoft.com/en-us/microsoft-edge/web-platform/password-reveal */
    ${$adornmentVariant === 'passwordMask' &&
    `&::-ms-reveal {
      display: none;
    }`}

    /* disable spinner arrows for number inputs (Chrome, Safari, Edge, Opera) */
    /* we tried to find a better way to handle this (https://jira.int.ally.com/browse/WEB-37093)
       but this seems to be the accepted approach */
    /* https://www.w3schools.com/howto/howto_css_hide_arrow_number.asp */
    /* https://css-tricks.com/snippets/css/turn-off-number-input-spinners/ */
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* disable spinner arrows for number inputs (Firefox) */
    /* https://www.w3schools.com/howto/howto_css_hide_arrow_number.asp */
    -moz-appearance: textfield;
  `}
`;
const getInputType = ({ inputType, adornment, isMasked, }) => {
    return getAdornmentInputType({ adornment, isMasked }) || inputType || 'text';
};
const TextInput = React.forwardRef((props, ref) => {
    const { id, name, inputType, value, onChange, onBlur, onFocus, errorNotification, characterCounter, adornment, inputSelect, isPrivate = false } = props, others = __rest(props, ["id", "name", "inputType", "value", "onChange", "onBlur", "onFocus", "errorNotification", "characterCounter", "adornment", "inputSelect", "isPrivate"]);
    const inputWrapperRef = useRef(null);
    const inputRef = useRef(typeof ref === 'object' && (ref === null || ref === void 0 ? void 0 : ref.current) ? ref.current : null);
    const combinedRef = (el) => {
        if (ref && typeof ref === 'object') {
            ref.current = el;
        }
        if (ref && typeof ref === 'function') {
            ref(el);
        }
        inputRef.current = el;
    };
    const { setIsBlurred, setInputRef, setOnBlurRelatedTarget } = useAdornmentState();
    React.useEffect(() => {
        setInputRef(inputRef);
    }, []);
    const isPasswordMask = adornment === 'passwordMask';
    const { isMasked } = usePasswordMaskAdornment();
    const privateAttributesOpts = isPasswordMask ? 'Password' : undefined;
    const privateAttrs = isPasswordMask || inputType === 'password' || isPrivate
        ? getPrivateAttributes({ alias: privateAttributesOpts })
        : {};
    const handleOnBlur = (e) => {
        if (onBlur)
            onBlur(e);
        setIsBlurred(true);
        setOnBlurRelatedTarget(e.relatedTarget);
    };
    const handleOnFocus = (e) => {
        if (onFocus)
            onFocus(e);
        setIsBlurred(false);
        setOnBlurRelatedTarget(null);
    };
    const inputTypeProps = inputType === 'number'
        ? {}
        : {
            maxLength: props.maxLength,
            minLength: props.minLength,
        };
    const [inputSelectA11yProps, setInputSelectA11yProps] = useState({});
    const safeInputType = getInputType({
        inputType,
        adornment,
        isMasked,
    });
    const adornmentProps = parseAdornmentOpts(adornment);
    const { position: adornmentPosition } = useAdornmentState();
    return (React.createElement(FormTextWrapper, { ref: inputWrapperRef },
        React.createElement(Adornment, Object.assign({ inputValue: value }, adornmentProps),
            React.createElement(StyledInput, Object.assign({ ref: combinedRef, type: safeInputType, id: id, name: name || id, value: value, onChange: onChange, onBlur: handleOnBlur, onFocus: handleOnFocus, "$hasCounter": inputType === 'number' ? false : !!(characterCounter === null || characterCounter === void 0 ? void 0 : characterCounter.show), "$adornmentVariant": adornmentProps.variant, "$adornmentPosition": adornmentProps.variant ? adornmentPosition : undefined }, privateAttrs, inputSelectA11yProps, ((errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.message) && {
                'data-allytm-error': errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.message,
            }), others, inputTypeProps)),
            React.createElement(React.Fragment, null, !!inputSelect && (React.createElement(InputSelect, Object.assign({ inputWrapperRef: inputWrapperRef, onA11yPropsUpdate: setInputSelectA11yProps, inputValue: value }, inputSelect)))))));
});
const TextInputWithProviders = React.forwardRef((props, ref) => (React.createElement(AdornmentProvider, null,
    React.createElement(TextInput, Object.assign({}, props, { ref: ref })))));
const TextInputWithMarginProps = styled(TextInputWithProviders)(margin);
export { TextInputWithMarginProps as TextInput };
//# sourceMappingURL=TextInput.js.map